import * as React from "react";

import type { VariantProps } from "class-variance-authority";

import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva } from "class-variance-authority";
import { X } from "lucide-react";

import { cn } from "../../utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) => (
  <SheetPrimitive.Overlay
    className={cn(
      "af-bg-overlay/80 af-fixed af-inset-0 af-z-50 data-[state=open]:af-animate-in data-[state=closed]:af-animate-out data-[state=closed]:af-fade-out-0 data-[state=open]:af-fade-in-0",
      className,
    )}
    {...props}
  />
);
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "af-fixed af-z-50 af-gap-4 af-bg-background af-p-6 af-shadow-lg af-transition af-ease-in-out data-[state=closed]:af-duration-300 data-[state=open]:af-duration-500 data-[state=open]:af-animate-in data-[state=closed]:af-animate-out",
  {
    variants: {
      side: {
        top: "af-inset-x-0 af-top-0 af-border-b af-border-border data-[state=closed]:af-slide-out-to-top data-[state=open]:af-slide-in-from-top",
        bottom:
          "af-inset-x-0 af-bottom-0 af-border-t af-border-border data-[state=closed]:af-slide-out-to-bottom data-[state=open]:af-slide-in-from-bottom",
        left: "af-inset-y-0 af-left-0 af-h-full af-w-3/4 af-border-r af-border-border data-[state=closed]:af-slide-out-to-left data-[state=open]:af-slide-in-from-left sm:af-max-w-sm",
        right:
          "af-inset-y-0 af-right-0 af-h-full af-w-3/4 af-border-l af-border-border data-[state=closed]:af-slide-out-to-right data-[state=open]:af-slide-in-from-right sm:af-max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends
    React.ComponentProps<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  withCloseButton?: boolean;
}

const SheetContent = ({
  side = "right",
  className,
  children,
  withCloseButton = true,
  ...props
}: SheetContentProps) => (
  <SheetPortal>
    <SheetOverlay className="af-bg-black/50" />
    <SheetPrimitive.Content
      className={cn(sheetVariants({ side }), className)}
      {...props}
    >
      {children}
      {withCloseButton && (
        <SheetPrimitive.Close className="af-absolute af-right-4 af-top-4 af-rounded-sm af-opacity-70 af-ring-offset-background af-transition-opacity hover:af-opacity-100 focus:af-outline-none focus:af-ring-2 focus:af-ring-ring focus:af-ring-offset-2 disabled:af-pointer-events-none data-[state=open]:af-bg-secondary">
          <X className="af-h-4 af-w-4" />
          <span className="af-sr-only">Close</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Content>
  </SheetPortal>
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "af-flex af-flex-col af-space-y-2 af-text-center sm:af-text-left",
      className,
    )}
    {...props}
  />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "af-flex af-flex-col-reverse sm:af-flex-row sm:af-justify-end sm:af-space-x-2",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) => (
  <SheetPrimitive.Title
    className={cn("af-text-lg af-font-semibold af-text-foreground", className)}
    {...props}
  />
);
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) => (
  <SheetPrimitive.Description
    className={cn("af-text-sm af-text-muted-foreground", className)}
    {...props}
  />
);
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
